/* =====================================================================
   CATEGORY PAGE REDESIGN — controller
   Doc:       docs/experiments/category-page-redesign.md
   Styles:    assets/collection-redesign.css (scoped to body.collection-redesign)
   Bootstrap: snippets/collection-redesign-experiment.liquid (owns the flag
              read and the body class; this file does everything after it)
   Loader:    layout/theme.liquid, deferred, after theme.js

   Experiment flag: 'category-page-redesign'   |   test variant: 'redesign'

   What it does
   ------------
   Layout is CSS. This file only does the things CSS cannot:
     - hides the sections the new header replaces (rich-text headings) and the
       slideshows above the grid
     - moves the merchant's own quick-links section into the header as the
       category pill row, and marks the current pill
     - orders the filter rail (Size, Width, Brand, Colour, Price, Availability,
       then anything else), opens Size/Brand/Colour, badges active counts
     - "Shop your size": chips delegate to the matching sidebar filter, so the
       theme's own handler does the filtering; the choice persists to
       localStorage and pre-applies on the next collection that stocks it
     - Quick add: expands the product's real variants and adds through the
       theme's AJAX cart
     - the mobile filter drawer and sort sheet, both driven off markup that is
       already on the page
     - PostHog event capture

   Everything is idempotent and re-run on every apply, because
   theme.CollectionFilters replaces the whole of #CollectionProductGrid on
   every filter, sort and page change.

   Every failure path lands on control: this file never hides control markup
   on its own, it only acts once the bootstrap has put
   'collection-redesign' on <body>.

   QA
   --
   ?ab_debug (or localhost) logs as [collection-redesign].
   Force the variant:
     posthog.featureFlags.overrideFeatureFlags({flags:{'category-page-redesign':'redesign'}})
   `window.__collectionRedesign.activate()` applies it by hand, ignoring the flag.

   Teardown
   --------
   Lost: delete this file, assets/collection-redesign.css, the four
   collection-redesign-* snippets and the collection-redesign-fit-name snippet,
   the two <script>/<link> lines in layout/theme.liquid, and the redesign
   renders/params in main-collection, product-listing and collection-sidebar.
   Won: see the doc, the body class moves server-side.
   ===================================================================== */
(function () {
  'use strict';

  var BODY_CLASS = 'collection-redesign';
  var SIZE_KEY = 'solelution_shop_size';
  var SIZE_PARAM = 'filter.v.option.size';

  /* Filter rail order. Anything not listed keeps the CSS default (50) and so
     sorts after these, rather than being dropped. */
  var FILTER_ORDER = {
    'filter.v.option.size': 1,
    'filter.v.option.width': 2,
    'filter.p.vendor': 3,
    'filter.v.option.colour': 4,
    'filter.v.option.color': 4,
    'price': 5,
    'filter.v.availability': 6
  };

  var OPEN_BY_DEFAULT = ['filter.v.option.size', 'filter.p.vendor', 'filter.v.option.colour', 'filter.v.option.color'];

  var DEBUG = (window.location.search.indexOf('ab_debug') !== -1) ||
    (window.location.hostname === 'localhost') ||
    (window.location.hostname.indexOf('127.0.0.1') === 0);

  var bound = false;
  var defaultsDone = false;

  function log() {
    if (!DEBUG || !window.console) return;
    try {
      console.log.apply(console, ['[collection-redesign]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function isActive() {
    return document.body && document.body.classList.contains(BODY_CLASS);
  }

  function collectionHandle() {
    var page = document.querySelector('.collection__page');
    var link = page && page.getAttribute('data-collection-link');
    if (link) return link.split('/').pop();
    var parts = window.location.pathname.split('/');
    var i = parts.indexOf('collections');
    return i > -1 && parts[i + 1] ? parts[i + 1] : window.location.pathname;
  }

  function capture(name, props) {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return;
    try { window.posthog.capture(name, props); }
    catch (e) { log('capture failed', name, e); }
  }

  function resultCount() {
    var el = document.querySelector('.cr-toolbar-zone .filtering__pagination p');
    var m = el && (el.textContent || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  }

  /* ---------------------------------------------------------------- */
  /* Layout helpers                                                     */
  /* ---------------------------------------------------------------- */

  function mainSectionWrapper() {
    var page = document.querySelector('.collection__page');
    if (!page) return null;
    return page.closest('.shopify-section') || page.parentElement;
  }

  /* The rich-text headings above the grid are what the new header replaces,
     and the slideshows are what pushes the products down the page. Only
     sections BEFORE the collection section are touched, so editorial content
     underneath the grid is left alone. */
  function hideReplacedSections() {
    var wrapper = mainSectionWrapper();
    if (!wrapper) return;

    var borrowed = null;
    var el = wrapper.previousElementSibling;
    while (el) {
      if (el.querySelector) {
        var h1 = el.querySelector('.rich__text-section h1');
        var isHeading = el.querySelector('.rich__text-section') &&
          (h1 || el.querySelector('.rich__text-section h2'));
        var isSlideshow = !!el.querySelector('.slideshow-section');
        // The brand-logo band on /collections/<brand>: an editorial-columns
        // section above the grid. The redesign leads with the vendor name as
        // the H1 instead, so the logo is 200px of duplication.
        var isBrandLogo = !!el.querySelector('.imageText__column-section');

        // Nearest rich-text <h1> above the grid, for the title fallback below.
        if (h1 && !borrowed && (h1.textContent || '').trim()) borrowed = h1.textContent.trim();

        if ((isHeading || isSlideshow || isBrandLogo) && !el.classList.contains('cr-hidden-section')) {
          el.classList.add('cr-hidden-section');
          log('hid section above the grid:',
            isSlideshow ? 'slideshow' : (isBrandLogo ? 'brand logo' : 'heading'));
        }
      }
      el = el.previousElementSibling;
    }

    // Some templates put the real page heading in a rich-text section rather
    // than at the top of collection.description, and collection.title is a
    // poorer version of it ("Mens" against "Mens Footwear"). If the header had
    // to fall back to collection.title, use the heading being hidden instead.
    var title = document.querySelector('.cr-header__title');
    if (borrowed && title && title.getAttribute('data-cr-title-source') === 'collection' &&
        title.textContent.trim() !== borrowed) {
      title.textContent = borrowed;
      log('title taken from the rich-text heading:', borrowed);
    }
  }

  /* The pill row is the merchant's own quick-links section wherever the
     template has one, so the curated links win. The Liquid fallback list (nav
     siblings) is only revealed when there is no quick-links section at all.

     The section is CLONED, not moved. The slot lives inside
     #CollectionProductGrid, whose innerHTML theme.CollectionFilters replaces
     wholesale on every filter and sort; moving the real section in there would
     delete the merchant's pills on the first filter click, permanently. The
     original stays put (hidden) and is re-cloned after each re-render. */
  function buildPills() {
    var slot = document.querySelector('[data-cr-pills-slot]');
    if (!slot) return;

    var here = window.location.pathname.replace(/\/$/, '');
    var fallback = slot.querySelector('[data-cr-pills-fallback]');

    if (!slot.querySelector('.quick__links-section')) {
      var wrapper = mainSectionWrapper();
      var sections = [].slice.call(document.querySelectorAll('.quick__links-section'));
      var chosen = null;

      for (var i = 0; i < sections.length; i++) {
        if (sections[i].closest('[data-cr-pills-slot]')) continue;
        // Only sections above the grid, and prefer the one that links here.
        if (wrapper && !(sections[i].compareDocumentPosition(wrapper) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
        if (!chosen) chosen = sections[i];
        if (sections[i].querySelector('a[href="' + here + '"], a[href="' + here + '/"]')) {
          chosen = sections[i];
          break;
        }
      }

      if (chosen && chosen.querySelector('.quick__link')) {
        var clone = chosen.cloneNode(true);
        clone.classList.remove('cr-hidden-section');
        slot.appendChild(clone);
        chosen.classList.add('cr-hidden-section');
        log('pills: cloned the quick-links section into the header');
      }
    }

    var moved = slot.querySelector('.quick__links-section');
    if (moved) {
      if (fallback) fallback.setAttribute('hidden', '');
      var links = moved.querySelectorAll('.quick__link');
      for (var j = 0; j < links.length; j++) {
        var a = links[j].querySelector('a');
        var path = a ? a.getAttribute('href') : null;
        if (path) path = path.replace(/\/$/, '');
        links[j].classList.toggle('cr-pill--current', path === here);
      }
    } else if (fallback) {
      fallback.removeAttribute('hidden');
    }
  }

  /* The dots are the theme's own swatches; moving them next to the colour name
     keeps one source of truth for what a colour looks like. */
  function placeColourSwatches() {
    var slots = document.querySelectorAll('[data-cr-colour]');
    for (var i = 0; i < slots.length; i++) {
      var card = slots[i].closest('.js-product-listing');
      var dots = card && card.querySelector('.prod-colors');
      if (dots && dots.parentElement !== slots[i]) slots[i].insertBefore(dots, slots[i].firstChild);
    }
  }

  /* Swym is app-injected. If it is not on the page the button would do
     nothing, so it stays hidden rather than lying to the shopper. */
  function revealWishlist() {
    if (!window.SwymCallbacks && !window._swat) return;
    var buttons = document.querySelectorAll('.cr-card__wishlist[hidden]');
    for (var i = 0; i < buttons.length; i++) buttons[i].removeAttribute('hidden');
    if (buttons.length) log('wishlist: revealed', buttons.length, 'buttons');
  }

  /* ---------------------------------------------------------------- */
  /* Filter rail                                                        */
  /* ---------------------------------------------------------------- */
  function decorateFilters() {
    var sidebar = document.getElementById('collection-sidebar');
    if (!sidebar) return;

    var headers = sidebar.querySelectorAll('[data-cr-filter]');

    for (var i = 0; i < headers.length; i++) {
      var header = headers[i];
      var key = header.getAttribute('data-cr-filter');
      var panel = document.getElementById(header.getAttribute('aria-controls'));

      var order = FILTER_ORDER[key];
      if (order) {
        header.style.setProperty('--cr-order', order);
        if (panel) panel.style.setProperty('--cr-order', order);
      }

      // A group with a single value cannot filter anything: that is how the
      // Brand group disappears on a brand collection, without hard-coding the
      // template.
      var options = panel ? panel.querySelectorAll('.c-accordion__inner > ul > li') : [];
      var isList = panel && panel.querySelector('.c-accordion__inner > ul');
      var useless = isList && options.length < 2;
      header.classList.toggle('cr-filter-useless', !!useless);
      if (panel) panel.classList.toggle('cr-filter-useless', !!useless);

      // Active count badge. Only written when the number actually changes:
      // this header is inside #CollectionProductGrid, so removing and
      // recreating the badge on every pass is a childList mutation that wakes
      // the observer, which calls apply() again, which rewrites the badge. That
      // loop is what re-opened a group the moment a shopper collapsed it.
      var active = panel ? panel.querySelectorAll('input[type="checkbox"]:checked').length : 0;
      var badge = header.querySelector('.cr-filter-badge');
      header.classList.toggle('cr-has-badge', active > 0);

      if (active > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'cr-filter-badge';
          var arrow = header.querySelector('.dropdown-arrow');
          if (arrow) header.insertBefore(badge, arrow);
          else header.appendChild(badge);
        }
        if (badge.textContent !== String(active)) badge.textContent = active;
      } else if (badge) {
        badge.parentNode.removeChild(badge);
      }

      // Size, Brand and Colour start open. The theme collapses every panel
      // after each AJAX re-render, so this runs on every pass, but never
      // against a shopper who has just closed the group by hand.
      if (OPEN_BY_DEFAULT.indexOf(key) !== -1 &&
          header.getAttribute('aria-expanded') !== 'true' &&
          !header.hasAttribute('data-cr-user-toggled') &&
          window.WAU && window.WAU.Accordion && panel) {
        try { window.WAU.Accordion.show(header, true); }
        catch (e) { log('could not open', key, e); }
      }
    }
  }

  /* Drawer furniture lives inside #collection-sidebar so it scrolls and clears
     with the filters. Rebuilt after each re-render; CSS hides it above 740px. */
  function buildDrawerFurniture() {
    var sidebar = document.getElementById('collection-sidebar');
    if (!sidebar) return;

    if (!sidebar.querySelector('.cr-drawer-head')) {
      var head = document.createElement('div');
      head.className = 'cr-drawer-head';
      head.innerHTML = '<span>Filter</span>' +
        '<button type="button" class="cr-drawer-close" data-cr-drawer-close aria-label="Close filters">&times;</button>';
      sidebar.insertBefore(head, sidebar.firstChild);
    }

    var foot = sidebar.querySelector('.cr-drawer-foot');
    if (!foot) {
      foot = document.createElement('div');
      foot.className = 'cr-drawer-foot';
      foot.innerHTML = '<button type="button" class="cr-drawer-clear" data-cr-drawer-clear>Clear</button>' +
        '<button type="button" class="cr-drawer-apply" data-cr-drawer-close></button>';
      sidebar.appendChild(foot);
    }

    var apply = foot.querySelector('.cr-drawer-apply');
    var n = resultCount();
    if (apply) {
      var label = n === null ? 'Show results' : 'Show ' + n + (n === 1 ? ' style' : ' styles');
      if (apply.textContent !== label) apply.textContent = label;
    }
  }

  /* The theme's sort control is a type-to-filter text input whose placeholder
     carries the active sort. Focusing it swaps that placeholder for
     data-generic-placeholder ("Type to filter"), so the control appears to
     forget what it is sorted by the moment you click it. In the redesign it
     reads as a dropdown, which makes that worse. Pointing the generic
     placeholder at the active sort keeps the label on screen; typing still
     filters the list. Re-run on every pass because renderFilters() rewrites
     the placeholder from the current option after each AJAX update. */
  function decorateSort() {
    var input = document.querySelector('[data-collection-sort-by] .js-hz-filter-input');
    if (!input) return;
    var current = document.querySelector('[data-collection-sort-by] li.current');
    var label = (current && current.getAttribute('data-placeholder')) || input.placeholder;
    if (label) {
      input.placeholder = label;
      input.setAttribute('data-generic-placeholder', label);
    }
  }

  function updateMobileBar() {
    var n = document.querySelectorAll('.cr-toolbar-zone .current_filters--filter').length;

    var badge = document.querySelector('[data-cr-drawer-count]');
    if (badge) {
      if (badge.textContent !== String(n)) badge.textContent = n;
      if (n > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    }

    // Same count on the theme's Filter button in the toolbar.
    var toggle = document.querySelector('.cr-toolbar-zone .js-collection-side-toggle');
    if (!toggle) return;
    var count = toggle.querySelector('.cr-toolbar-filter-count');
    if (n > 0) {
      if (!count) {
        count = document.createElement('span');
        count.className = 'cr-toolbar-filter-count';
        toggle.appendChild(count);
      }
      if (count.textContent !== String(n)) count.textContent = n;
    } else if (count) {
      count.parentNode.removeChild(count);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Filtering                                                          */
  /* ---------------------------------------------------------------- */

  /* The sidebar checkbox for a filter value. Everything the redesign filters
     with goes through these, so there is exactly one filter implementation on
     the page and no duplicate inputs in the form theme.js gathers. */
  function sidebarInput(param, value) {
    var inputs = document.querySelectorAll('#collection-sidebar input[type="checkbox"]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].name === param && inputs[i].value === value) return inputs[i];
    }
    return null;
  }

  function renderNow() {
    if (!window.theme || !theme.CollectionFilters) return false;
    try {
      var formData = theme.CollectionFilters.gatherForm();
      // theme.CollectionFilters.renderPage(new URLSearchParams(formData).toString());
      theme.CollectionFilters.renderPage(new URLSearchParams(formData).toString(), undefined, false);
      return true;
    } catch (e) {
      log('render failed', e);
      return false;
    }
  }

  function readStoredSize() {
    try { return window.localStorage.getItem(SIZE_KEY); } catch (e) { return null; }
  }

  function writeStoredSize(value) {
    try {
      if (value) window.localStorage.setItem(SIZE_KEY, value);
      else window.localStorage.removeItem(SIZE_KEY);
    } catch (e) {}
  }

  /* Best selling as the default sort, and the shopper's remembered size, are
     both set on the checkboxes first and rendered once, so the page is fetched
     a single time no matter how many defaults apply. */
  function applyDefaults() {
    if (defaultsDone) return;

    var bar = document.querySelector('[data-cr-size-bar]');
    var grid = document.getElementById('main-collection-product-grid');
    if (!grid) return;

    defaultsDone = true;

    var params = new URLSearchParams(window.location.search);
    var changed = false;

    if (!params.has('sort_by')) {
      var option = document.querySelector('[data-collection-sort-by] li[data-option-value="best-selling"]');
      if (option && !option.classList.contains('current')) {
        var siblings = option.parentNode.querySelectorAll('li');
        for (var i = 0; i < siblings.length; i++) {
          var cb = siblings[i].querySelector('input[type="checkbox"]');
          var on = siblings[i] === option;
          siblings[i].classList.toggle('current', on);
          if (cb) cb.checked = on;
        }
        var input = document.querySelector('[data-collection-sort-by] .js-hz-filter-input');
        if (input && option.dataset.placeholder) input.placeholder = option.dataset.placeholder;
        changed = true;
        log('defaulted sort to best selling');
      }
    }

    var stored = readStoredSize();
    if (bar && stored && !params.has(SIZE_PARAM)) {
      var chip = bar.querySelector('[data-cr-size-value="' + (window.CSS && CSS.escape ? CSS.escape(stored) : stored) + '"]');
      var box = sidebarInput(bar.getAttribute('data-cr-size-param') || SIZE_PARAM, stored);
      if (chip && box && !box.checked && !box.disabled) {
        box.checked = true;
        var wrap = box.closest('.js-collection-side-filter');
        if (wrap) wrap.classList.add('current');
        changed = true;
        log('pre-applied stored size', stored);
      } else if (!chip) {
        log('stored size', stored, 'is not stocked in this collection, ignoring');
      }
    }

    if (changed) renderNow();
  }

  /* ---------------------------------------------------------------- */
  /* Cart                                                               */
  /* ---------------------------------------------------------------- */
  function isNarrow() {
    return window.matchMedia && window.matchMedia('(max-width: 740px)').matches;
  }

  /* On a phone the size buttons stack one per row, because labels carry the
     width too ("EU 41 · Regular"). Below 740px the same variants become a
     select instead, built from the buttons so there is one source of truth for
     the labels and the sold-out state. Built lazily, on first open, so it costs
     nothing on the cards a shopper never touches, and a re-rendered grid simply
     builds it again. Sold-out wording matches the PDP size dropdown. */
  function ensureSizeSelect(box) {
    if (!isNarrow()) return;

    var sizes = box.querySelector('[data-cr-sizes]');
    if (!sizes || sizes.querySelector('[data-cr-size-select]')) return;

    var buttons = sizes.querySelectorAll('.cr-card__size');
    if (!buttons.length) return;

    var select = document.createElement('select');
    select.className = 'cr-card__sizes-select';
    select.setAttribute('data-cr-size-select', '');
    select.setAttribute('aria-label', 'Choose a size');

    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    placeholder.textContent = 'Choose a size';
    select.appendChild(placeholder);

    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var option = document.createElement('option');
      option.value = button.getAttribute('data-cr-variant-id') || '';
      option.textContent = (button.getAttribute('data-cr-size') || button.textContent).trim() +
        (button.disabled ? ' - Sold out' : '');
      if (button.disabled) option.disabled = true;
      select.appendChild(option);
    }

    sizes.appendChild(select);
    log('quick add: built the size dropdown for', box.getAttribute('data-cr-product-id'));
  }

  function cartConfig() {
    var el = document.getElementById('cart-config');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  function addVariantToCart(variantId, button) {
    var config = cartConfig();
    var shopifyCart = window.Shopify && Shopify.theme && Shopify.theme.cart;
    var ajaxCart = window.Shopify && Shopify.theme && Shopify.theme.ajaxCart;

    // Shopify.theme.cart.addItem throws on anything but a Number, and the id
    // arrives here as a data attribute string.
    var id = parseInt(variantId, 10);
    if (!id) { log('quick add: no usable variant id', variantId); return; }

    if (button) button.classList.add('is-adding');

    function finish() {
      if (button) button.classList.remove('is-adding');
    }

    // Last resort so an add never silently does nothing: post it ourselves and
    // send the shopper to the cart, where they can see what happened.
    function fallback(why) {
      log('falling back to /cart/add.js:', why);
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      }).then(function () {
        window.location.href = config.cart_url || '/cart';
      }).catch(function (e) {
        finish();
        log('fallback add failed', e);
      });
    }

    if (!shopifyCart || typeof shopifyCart.addItem !== 'function') {
      fallback('theme cart API unavailable');
      return;
    }

    shopifyCart.addItem(id, { quantity: 1 })
      .then(function () {
        return shopifyCart.getCart();
      })
      .then(function (cart) {
        finish();
        if (!ajaxCart) return;
        try { ajaxCart.updateView(config, cart); } catch (e) { log('updateView failed', e); }
        try { ajaxCart.showDrawer(config); } catch (e) { log('showDrawer failed', e); }
      })
      .catch(function (e) {
        finish();
        fallback(e && (e.message || e.status || e));
      });
  }

  /* ---------------------------------------------------------------- */
  /* Events                                                             */
  /* ---------------------------------------------------------------- */
  function bindEvents() {
    if (bound) return;
    bound = true;

    document.addEventListener('click', function (event) {
      if (!isActive()) return;

      // ---- Shop your size -------------------------------------------------
      var chip = event.target.closest && event.target.closest('.cr-size-chip');
      if (chip) {
        var bar = chip.closest('[data-cr-size-bar]');
        var param = (bar && bar.getAttribute('data-cr-size-param')) || SIZE_PARAM;
        var value = chip.getAttribute('data-cr-size-value');
        var wasActive = chip.getAttribute('data-cr-size-active') === 'true';
        var countEl = chip.querySelector('.cr-size-chip__count');

        writeStoredSize(wasActive ? null : value);
        capture('collection_size_selected', {
          collection: collectionHandle(),
          size: value,
          results: countEl ? parseInt(countEl.textContent, 10) : null
        });

        // Delegate to the sidebar so the theme owns the filtering. With no
        // sidebar on this template the href is a real Shopify filter URL, so
        // letting the click through is the correct fallback.
        var input = sidebarInput(param, value);
        var link = input && input.closest('.js-collection-side-filter');
        if (link) {
          event.preventDefault();
          link.click();
        }
        return;
      }

      // ---- See more / See less on long size lists ------------------------
      // Only a class on the band; the collapsed list itself is rendered by
      // Liquid. Deliberately not remembered across a filter re-render: picking
      // a size re-renders the band, and it should come back tidy with the
      // chosen size showing rather than stay open at sixty chips.
      var more = event.target.closest && event.target.closest('[data-cr-size-more]');
      if (more) {
        event.preventDefault();
        var moreBar = more.closest('[data-cr-size-bar]');
        if (!moreBar) return;
        var expanded = moreBar.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        var moreLabel = more.querySelector('[data-cr-size-more-label]');
        if (moreLabel) {
          moreLabel.textContent = expanded
            ? 'See less'
            : 'See more (' + (more.getAttribute('data-cr-size-more-count') || '') + ')';
        }
        return;
      }

      var clear = event.target.closest && event.target.closest('[data-cr-size-clear]');
      if (clear) {
        writeStoredSize(null);
        return;
      }

      // ---- Quick add ------------------------------------------------------
      var toggle = event.target.closest && event.target.closest('[data-cr-quickadd-toggle]');
      if (toggle) {
        event.preventDefault();
        var box = toggle.closest('[data-cr-quickadd]');
        var sizes = box && box.querySelector('[data-cr-sizes]');
        if (!sizes) return;
        var opening = sizes.hasAttribute('hidden');
        if (opening) {
          ensureSizeSelect(box);
          sizes.removeAttribute('hidden');
        } else {
          sizes.setAttribute('hidden', '');
        }
        box.classList.toggle('is-open', opening);
        toggle.textContent = opening ? 'Close' : 'Quick add';
        if (opening) {
          capture('collection_quick_add_opened', { product_id: box.getAttribute('data-cr-product-id') });
        }
        return;
      }

      var size = event.target.closest && event.target.closest('.cr-card__size');
      if (size && !size.disabled) {
        event.preventDefault();
        var quick = size.closest('[data-cr-quickadd]');
        capture('collection_quick_add_atc', {
          product_id: quick ? quick.getAttribute('data-cr-product-id') : null,
          variant_id: size.getAttribute('data-cr-variant-id'),
          size: size.getAttribute('data-cr-size')
        });
        addVariantToCart(size.getAttribute('data-cr-variant-id'), size);
        return;
      }

      var direct = event.target.closest && event.target.closest('[data-cr-quickadd-direct]');
      if (direct && !direct.disabled) {
        event.preventDefault();
        var holder = direct.closest('[data-cr-quickadd]');
        capture('collection_quick_add_atc', {
          product_id: holder ? holder.getAttribute('data-cr-product-id') : null,
          variant_id: direct.getAttribute('data-cr-variant-id'),
          size: null
        });
        addVariantToCart(direct.getAttribute('data-cr-variant-id'), direct);
        return;
      }

      // ---- Wishlist (Swym owns the save itself) ---------------------------
      var heart = event.target.closest && event.target.closest('.cr-card__wishlist');
      if (heart) {
        heart.classList.add('is-saved');
        capture('collection_wishlist_added', { product_id: heart.getAttribute('data-product-id') });
        return;
      }

      // ---- Fitting CTAs ---------------------------------------------------
      var fitting = event.target.closest && event.target.closest('[data-cr-fitting-cta]');
      if (fitting) {
        capture('collection_fitting_cta_clicked', {
          collection: collectionHandle(),
          destination: fitting.getAttribute('data-cr-fitting-cta')
        });
        return;
      }

      // ---- Filter clicks (measurement only, the theme still handles them) --
      var filter = event.target.closest && event.target.closest('.js-collection-side-filter');
      if (filter) {
        var checkbox = filter.querySelector('input[type="checkbox"]');
        if (checkbox) {
          capture('collection_filter_applied', {
            collection: collectionHandle(),
            filter: checkbox.name,
            value: checkbox.value
          });
        }
        return;
      }

      // ---- A group the shopper opened or closed stays that way -------------
      var accordionHeader = event.target.closest && event.target.closest('#collection-sidebar .js-accordion-header');
      if (accordionHeader) {
        accordionHeader.setAttribute('data-cr-user-toggled', '');
      }

      // ---- Mobile drawer and sort sheet -----------------------------------
      // Both the bottom bar and the theme's own Filter button in the toolbar
      // open the drawer. The theme has its own listener bound directly to that
      // button which toggles `open` on #collection-sidebar and swaps the grid
      // spans; under the redesign the rail's visibility and the grid columns
      // are set in CSS, so that runs harmlessly and only writes its
      // localStorage value.
      if (event.target.closest('[data-cr-drawer-open]') ||
          event.target.closest('.js-collection-side-toggle')) {
        event.preventDefault();
        openDrawer();
        return;
      }

      if (event.target.closest('[data-cr-drawer-close]') || event.target.closest('.cr-drawer-scrim')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.target.closest('[data-cr-drawer-clear]')) {
        event.preventDefault();
        var clearAll = document.querySelector('.cr-toolbar-zone .current_filters--clear');
        closeDrawer();
        if (clearAll) clearAll.click();
        else window.location.href = window.location.pathname;
        return;
      }

      if (event.target.closest('[data-cr-sort-open]')) {
        event.preventDefault();
        toggleSortSheet();
        return;
      }

      // A sort option closes the sheet; the theme handles the sort itself.
      if (event.target.closest('[data-collection-sort-by] li')) {
        document.body.classList.remove('cr-sort-open');
      }
    });

    document.addEventListener('change', function (event) {
      if (!isActive()) return;

      var select = event.target.closest && event.target.closest('[data-cr-size-select]');
      if (!select || !select.value) return;

      var quick = select.closest('[data-cr-quickadd]');
      var chosen = select.options[select.selectedIndex];
      capture('collection_quick_add_atc', {
        product_id: quick ? quick.getAttribute('data-cr-product-id') : null,
        variant_id: select.value,
        size: chosen ? chosen.textContent.replace(' - Sold out', '') : null
      });
      addVariantToCart(select.value, select);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      closeDrawer();
      document.body.classList.remove('cr-sort-open');
    });

  }

  function openDrawer() {
    document.body.classList.add('cr-drawer-open');
    if (!document.querySelector('.cr-drawer-scrim')) {
      var scrim = document.createElement('button');
      scrim.type = 'button';
      scrim.className = 'cr-drawer-scrim';
      scrim.setAttribute('aria-label', 'Close filters');
      document.body.appendChild(scrim);
    }
  }

  function closeDrawer() {
    document.body.classList.remove('cr-drawer-open');
    var scrim = document.querySelector('.cr-drawer-scrim');
    if (scrim) scrim.parentNode.removeChild(scrim);
  }

  function toggleSortSheet() {
    var open = document.body.classList.toggle('cr-sort-open');
    var list = document.querySelector('[data-collection-sort-by] .js-hz-filter-list');
    if (list) list.classList.toggle('open', open);
  }

  /* ---------------------------------------------------------------- */
  /* Apply                                                              */
  /* ---------------------------------------------------------------- */
  function apply() {
    if (!isActive()) return;

    hideReplacedSections();
    buildPills();
    placeColourSwatches();
    revealWishlist();
    decorateFilters();
    decorateSort();
    buildDrawerFurniture();
    updateMobileBar();

    // The description only becomes measurable once the header is visible, and
    // the theme decides whether to show Read more from that measurement.
    if (window.WAU && window.WAU.CollectionDescription) {
      try { window.WAU.CollectionDescription.evaluate(); }
      catch (e) { log('description evaluate failed', e); }
    }

    applyDefaults();
  }

  function activate() {
    if (!document.body) return;
    document.body.classList.add(BODY_CLASS);
    bindEvents();
    apply();

    // The bootstrap's observer re-reads the flag after every AJAX re-render and
    // calls back in here, but it debounces on the grid alone. Watching from
    // this side too keeps the pills, swatches and rail correct if anything else
    // on the page rewrites the grid.
    if (!activate._observing) {
      var root = document.getElementById('CollectionProductGrid');
      if (root && 'MutationObserver' in window) {
        activate._observing = true;
        var t;
        new MutationObserver(function () {
          clearTimeout(t);
          t = setTimeout(apply, 100);
        }).observe(root, { childList: true, subtree: true });
      }
    }
  }

  window.__collectionRedesign = { activate: activate, apply: apply };

  function start() {
    // The bootstrap may have resolved the flag before this deferred file ran.
    if (isActive() || window.__collectionRedesignActive) activate();
    document.addEventListener('collection-redesign:active', activate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
